import { Link } from "react-router-dom";
import { ArrowRight, Compass, Handshake, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Odometer } from "@/components/Odometer";
import { RevealHeading } from "@/components/RevealHeading";
import { MotionInView } from "@/components/MotionInView";
import { company, stats, valueProps } from "@/content/site";
import { peopleGroups } from "@/content/people";

const principles = [
  {
    icon: Target,
    title: "Our mission",
    body: "Make tanker freight predictable - give brokers and shippers a carrier they can trust, give drivers steady miles, and give everyone a partner who picks up the phone.",
  },
  {
    icon: Compass,
    title: "How we work",
    body: "We take loads from brokers and shippers and haul them with our own trucks and vetted drivers - modern stainless tankers, live ELD tracking, and dispatch that stays ahead of the load.",
  },
  {
    icon: Handshake,
    title: "Who we hire",
    body: "Company drivers who want respect and steady miles, and owner-operators who want their truck kept loaded. People are the company - we treat them that way.",
  },
];

export default function About() {
  return (
    <>
      <Seo title={`About | ${company.name}`} description={company.description} />

      {/* HEADER */}
      <section className="relative overflow-hidden bg-brand-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 10px)",
          }}
        />
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:py-24">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">About us</div>
          <RevealHeading
            as="h1"
            text="A tanker carrier built around the load - and the people who move it."
            className="mb-5 max-w-3xl text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.05]"
          />
          <p className="max-w-2xl text-lg text-brand-200">{company.description}</p>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-b border-brand-100 bg-brand-50">
        <div className="mx-auto grid max-w-[1200px] gap-4 px-6 py-10 sm:grid-cols-3 sm:gap-6">
          {[
            { v: stats.years, l: "Years moving freight" },
            { v: stats.lanes, l: "States covered" },
            { v: stats.onTime, l: "On-time delivery" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <div className="text-3xl font-bold text-accent-500 lg:text-4xl">
                <Odometer value={s.v} />
              </div>
              <div className="mt-1 text-xs text-brand-500 lg:text-sm">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-6 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Our story</div>
            <RevealHeading text="Family-owned, professionally run." className="mb-5 text-3xl lg:text-4xl" />
            <div className="space-y-4 text-lg text-brand-600">
              <p>
                We are a family-owned transportation company specializing in the safe
                and dependable movement of liquid-bulk products. From the beginning,
                our focus has been straightforward: protect what we haul, communicate
                clearly, and earn trust one load at a time.
              </p>
              <p>
                The company is led by brothers whose professional background in Big
                Four accounting helped shape the way we operate today. That experience
                brought a disciplined approach to financial stewardship, process
                management, accountability, and long-range planning. In our business,
                that means thoughtful investment in equipment and people, careful
                attention to operational detail, and a commitment to building the kind
                of organization customers and employees can rely on for the long term.
              </p>
              <p>
                We believe family ownership still matters. It creates personal
                accountability, faster decision-making, and a long-term view of
                relationships. Combined with professional management discipline, it
                helps us deliver the safety, consistency, and service our customers
                expect in liquid-bulk transportation.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-brand-100 bg-brand-900">
              <img
                src="/brand/careers/owner-operators.jpg"
                alt="An owner standing in front of a Bull and Bear tractor and stainless tanker"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/40 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PEOPLE & SHOP */}
      <section className="bg-brand-900 py-20 text-white lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 max-w-3xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">People & shop</div>
            <RevealHeading text="The team behind every mile." className="text-3xl text-white lg:text-4xl" />
            <p className="mt-4 text-lg text-brand-200">
              A tanker load does not move because of one person. It takes dispatch,
              drivers, safety, accounting, office support, and the maintenance shop
              working in the same direction.
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {peopleGroups.map((group, i) => (
              <MotionInView key={group.title} delay={(i % 5) * 0.08} className="h-full">
                <div className="h-full overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-accent-500/40">
                  <div className="relative aspect-[4/3] overflow-hidden bg-white/10">
                    <img src={group.image} alt={group.imageAlt} loading="lazy" className="h-full w-full object-cover opacity-90" />
                    <div className="absolute left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-950/80 text-accent-300">
                      <group.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="mb-2 text-lg font-semibold text-white">{group.title}</h3>
                    <p className="text-sm text-brand-200">{group.body}</p>
                  </div>
                </div>
              </MotionInView>
            ))}
          </div>
          <Reveal className="mt-8">
            <Link to="/people" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              View people & shop page <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-brand-50 py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 max-w-2xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">What drives us</div>
            <RevealHeading text="Mission, method, and people." className="text-3xl lg:text-4xl" />
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {principles.map((p, i) => (
              <MotionInView key={p.title} delay={(i % 3) * 0.1} className="h-full">
                <div className="h-full rounded-lg border border-brand-100 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-accent-500/40 hover:shadow-md">
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-accent-500/10 text-accent-500">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-2 text-lg">{p.title}</h3>
                  <p className="text-sm text-brand-600">{p.body}</p>
                </div>
              </MotionInView>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES (shared with Home value props) */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 max-w-2xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Why shippers stay</div>
            <RevealHeading text="The carrier your freight deserves." className="text-3xl lg:text-4xl" />
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {valueProps.map((v, i) => {
              const Icon = v.icon;
              return (
                <MotionInView key={v.title} delay={(i % 4) * 0.08} className="h-full">
                  <div className="h-full rounded-lg border border-brand-100 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-accent-500/40 hover:shadow-md">
                    <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-900 text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mb-2 text-lg">{v.title}</h3>
                    <p className="text-sm text-brand-500">{v.body}</p>
                  </div>
                </MotionInView>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-900 py-16 text-white">
        <Reveal className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div>
            <RevealHeading text="Let's move it right." className="text-3xl lg:text-4xl" />
            <p className="mt-2 max-w-xl text-brand-300">
              Brokers and shippers looking for capacity, or drivers looking for a
              home - we'd like to hear from you.
            </p>
          </div>
          <div className="flex w-full shrink-0 flex-wrap gap-3 md:w-auto">
            <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Get a quote <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
            <Link
              to="/careers"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "w-full justify-center border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
              )}
            >
              See careers
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
